import React from "react";
import { getIconByName } from "./getIconByName";

/** Possible button variants. */
export type ButtonVariant = "brand" | "secondary" | "refresh" | "danger" | "dashed" | "outlined" | "active";

/** Mapping each variant to Tailwind classes and theme variables. */
const variantClasses: Record<ButtonVariant, string> = {
    brand: `
    bg-semantic-bg-brand-bold-secondary
    text-white
    hover:opacity-90
    active:opacity-80
  `,
    secondary: `
    bg-semantic-bg-primary
    border border-solid
    border-[var(--color-primitive-gray-300)]
    text-semantic-text-primary
    hover:bg-semantic-state-active
    active:bg-semantic-bg-primary
    shadow-sm
  `,
    danger: `
    bg-semantic-bg-danger-secondary
    border
    border-semantic-border-danger-primary
    text-semantic-text-danger
    hover:bg-semantic-hover-danger
    active:bg-semantic-bg-danger-primary
    shadow-sm
  `,
    refresh: `
    bg-transparent
    text-semantic-text-primary
    hover:bg-semantic-state-active
    rounded-full
    border-none
  `,
    dashed: `
    bg-semantic-bg-primary
    text-semantic-text-primary
    hover:bg-semantic-state-active
    border-2 border-dashed  border-semantic-border-secondary
    `,
    outlined: `
    gap-[8px]
    rounded-md
    border
    border-semantic-border-brand-secondary
    text-semantic-text-primary
    bg-transparent
    hover:bg-semantic-state-active
    active:bg-semantic-bg-primary-alt
  `,
  active: `
    bg-semantic-state-active-brand
    border border-semantic-border-brand-secondary
    text-semantic-text-primary
    rounded-lg
  `,
};

/** Button shape: "filled" or "ghost". */
export type ButtonShape = "filled" | "ghost";

const shapeClasses: Record<ButtonShape, string> = {
    filled: `
    /* no border rule*/
  `,
    ghost: `
    bg-transparent
    hover:bg-semantic-state-active
  `,
};

/** Available button sizes */
export type ButtonSize = "xsm" | "sm" | "md" | "lg";

/**
 * - `sm` => h-8 (32px)
 * - `md` => ~40px (si no hay customHeight)
 * - `lg` => 48px
 */
const sizeClasses: Record<ButtonSize, string> = {
    xsm: "h-5 px-2 gap-1 text-sm",
    sm: "h-8 px-2 gap-1 text-sm",
    md: "py-1.5 px-3 gap-2 text-sm",
    lg: "h-12 px-4 gap-2 text-base",
};

/** Props for the GenericButton component. */
export interface GenericButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Texto o JSX para el label. */
    label?: React.ReactNode;
    variant?: ButtonVariant;
    shape?: ButtonShape;
    size?: ButtonSize;

    /**
     * Slots para meter íconos u otros elementos manualmente.
     * (Estos se renderizan primero).
     */
    slotLeft?: React.ReactNode;
    slotRight?: React.ReactNode;

    /**
     * Si true, se oculta el label (btn icon-only).
     */
    isIconOnly?: boolean;

    /**
     * Estado de "loading": deshabilita el botón y reduce interacciones.
     */
    loading?: boolean;

    /**
     * Alto custom (e.g. "40px", "3rem").
     * Si no se da y size=md => 40px por defecto.
     */
    customHeight?: string;

    /** Ícono "manual" en JSX (ya existente) */
    icon?: React.ReactNode;
    /** Posición del ícono "manual": "left" o "right". */
    iconPosition?: "left" | "right";

    /**
     * NUEVO: Nombre del ícono para la izquierda
     * (por ejemplo "refresh", "arrowLeft", etc.)
     */
    iconLeftName?: string;

    /**
     * NUEVO: Nombre del ícono para la derecha
     */
    iconRightName?: string;
}

/** Generic button component */
// export const GenericButton: React.FC<GenericButtonProps> = ({
export const GenericButton: React.ForwardRefExoticComponent<GenericButtonProps & React.RefAttributes<HTMLButtonElement>> = React.forwardRef<HTMLButtonElement, GenericButtonProps>(({
    label,
    variant = "brand",
    shape = "filled",
    size = "md",
    slotLeft,
    slotRight,
    isIconOnly = false,
    loading = false,
    disabled,
    customHeight,
    style,
    className,
    // Ícono manual
    icon,
    iconPosition = "left",

    // NUEVO: strings para mapear a íconos
    iconLeftName,
    iconRightName,

    ...props
}, ref) => {
// }) => {
    const effectiveShape = variant === "outlined" ? "ghost" : shape;
    
    const finalClasses = `
    inline-flex items-center justify-center
    rounded-sm
    transition-all duration-200 ease-out
    ${shapeClasses[effectiveShape] ?? ""}
    ${variantClasses[variant] ?? ""}
    ${sizeClasses[size] ?? ""}
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

   // Combine with custom className if provided
   const combinedClassName = className ? `${finalClasses} ${className}` : finalClasses;

    // Si size=md y no se pasa customHeight => 40px
    const defaultHeight = size === "md" && !customHeight ? "40px" : undefined;
    const buttonStyle: React.CSSProperties = {
        ...style,
        height: customHeight || defaultHeight,
    };

    // Obtenemos íconos segun los nombres
    const leftIconByName = iconLeftName ? getIconByName(iconLeftName) : null;
    const rightIconByName = iconRightName ? getIconByName(iconRightName) : null;

    return (
        <button
            type="button"
            ref={ref}
            // className={finalClasses}
            className={combinedClassName}
            disabled={disabled || loading}
            style={buttonStyle}
            {...props}
        >
        {/*
            1) If loading, show spinner first.
        */}
            {loading && (
                <svg
                className="animate-spin h-4 w-4 mr-2 text-current"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                />
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
                </svg>
            )}

            {/* Keeps the label when loading */}
            {loading && (
                <span className="mx-1 whitespace-nowrap">{label}</span>
            )}

            {/* slotLeft: si el padre pasa un icono manual (más viejo) */}
            {slotLeft && <span className="inline-flex items-center">{slotLeft}</span>}

            {/* Nuevo iconLeftName */}
            {leftIconByName && (
                <span className="inline-flex items-center mr-1 text-current">
                    {leftIconByName}
                </span>
            )}

            {/* Lógica antigua de "icon" */}
            {icon && iconPosition === "left" && (
                <span className="inline-flex items-center mr-1 text-current">{icon}</span>
            )}

            {/* Si no es icon-only, mostramos la label */}
            {!isIconOnly && label && !loading && (
                <span className="mx-1 whitespace-nowrap">{label}</span>
            )}

            {icon && iconPosition === "right" && (
                <span className="inline-flex items-center ml-1 text-current">{icon}</span>
            )}

            {/* Nuevo iconRightName */}
            {rightIconByName && (
                <span className="inline-flex items-center ml-1 text-current">
                    {rightIconByName}
                </span>
            )}

            {slotRight && (
                <span className="inline-flex items-center">{slotRight}</span>
            )}
        </button>
    );
// };
});

GenericButton.displayName = "GenericButton";

export default GenericButton;


// export default GenericButton;
